import React, { useState, useEffect, useCallback } from "react";
import { PLANS_API } from "../config/api";
import "../styles/AdminSubscribe.css";
import { showConfirm } from "../utils/customDialog";

function AdminSubscribe() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [activeCategoryTab, setActiveCategoryTab] = useState("all"); // "all" | "india" | "abroad"

  // ================= POPUP =================
  const showPopup = useCallback((msg, type = "success") => {
    setPopup({ msg, type });
    setTimeout(() => setPopup(null), 2000);
  }, []);

  // ================= FETCH PLANS =================
  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(PLANS_API);
      const data = await res.json();

      const apiPlans = Array.isArray(data) ? data : [];

      const safePlans = apiPlans.map((p) => {
        const isAbroad = p.plan_type === "abroad" ||
          (p.monthly_title || "").toLowerCase().includes("overseas") ||
          (p.annual_title || "").toLowerCase().includes("overseas");

        return {
          id: p.id,
          planType: isAbroad ? "abroad" : (p.plan_type || "india"),
          monthlyTitle: p.monthly_title || "",
          annualTitle: p.annual_title || "",
          description: p.description || "",
          monthlyPrice: Number(p.monthly_price || 0),
          annualPrice: Number(p.annual_price || 0),
          abroadMonthlyPrice: Number(p.abroad_monthly_price || p.monthly_price || 0),
          abroadAnnualPrice: Number(p.abroad_annual_price || p.annual_price || 0),
          features: Array.isArray(p.features)
            ? p.features
            : JSON.parse(p.features || "[]"),
          isCurrent: Number(p.isFree) === 1,
        };
      });

      setPlans(safePlans);
      showPopup("Plans loaded");
    } catch (err) {
      console.error(err);
      showPopup("Load error", "error");
    } finally {
      setLoading(false);
    }
  }, [showPopup]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // ================= ADD PLAN =================
  const handleAdd = () => {
    const defaultType = activeCategoryTab === "abroad" ? "abroad" : "india";
    setPlans((prev) => [
      ...prev,
      {
        id: `new_${Date.now()}`,
        planType: defaultType,
        monthlyTitle: defaultType === "abroad" ? "1 YEAR SUBSCRIPTION + OVERSEAS" : "",
        annualTitle: defaultType === "abroad" ? "2 YEARS SUBSCRIPTION + OVERSEAS" : "",
        description: defaultType === "abroad" ? "Receive spiritual magazines monthly delivered via Overseas Postal / Air Mail service." : "",
        monthlyPrice: defaultType === "abroad" ? 3000 : 600,
        annualPrice: defaultType === "abroad" ? 6000 : 1200,
        abroadMonthlyPrice: defaultType === "abroad" ? 3000 : 0,
        abroadAnnualPrice: defaultType === "abroad" ? 6000 : 0,
        features: ["Printed magazines monthly", "Digital access to magazines"],
        isCurrent: false,
        isNew: true,
      },
    ]);
    showPopup(`New ${defaultType === "abroad" ? "Abroad" : "India"} plan added`);
  };

  // ================= UPDATE PLAN =================
  const updatePlan = (id, field, value) => {
    setPlans((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const updated = { ...p, [field]: value };
        // Sync abroad prices if planType is abroad
        if (field === "abroadMonthlyPrice" && updated.planType === "abroad") {
          updated.monthlyPrice = value;
        } else if (field === "abroadAnnualPrice" && updated.planType === "abroad") {
          updated.annualPrice = value;
        } else if (field === "monthlyPrice" && updated.planType === "abroad") {
          updated.abroadMonthlyPrice = value;
        } else if (field === "annualPrice" && updated.planType === "abroad") {
          updated.abroadAnnualPrice = value;
        }
        return updated;
      })
    );
  };

  // ================= EDIT PLAN =================
  const handleEditPlan = (plan) => {
    showPopup(`Editing Plan: ${plan.monthlyTitle || plan.id}`);
    const planCardEl = document.getElementById(`plan-card-${plan.id}`);
    if (planCardEl) {
      planCardEl.scrollIntoView({ behavior: "smooth", block: "center" });
      const firstInput = planCardEl.querySelector("input[type='text'], input[type='number']");
      if (firstInput) {
        firstInput.focus();
      }
    }
  };

  // ================= DELETE PLAN =================
  const deletePlan = async (id) => {
    const ok = await showConfirm({
      title: "Delete Plan",
      message: "Are you sure you want to delete this subscription plan?",
      subMessage: "This plan will be removed from the active subscription options.",
      confirmText: "Delete Plan",
      type: "danger"
    });
    if (!ok) return;
    setPlans((prev) => prev.filter((p) => p.id !== id));
    showPopup("Plan deleted");
  };

  // ================= FEATURES =================
  const addFeature = (id) => {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, features: [...p.features, ""] }
          : p
      )
    );
  };

  const updateFeature = (id, index, value) => {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              features: p.features.map((f, i) =>
                i === index ? value : f
              ),
            }
          : p
      )
    );
  };

  const deleteFeature = (id, index) => {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              features: p.features.filter((_, i) => i !== index),
            }
          : p
      )
    );
  };

  // ================= SAVE =================
  const handleSave = async () => {
    try {
      const payload = plans.map((p) => ({
        id: p.isNew ? null : p.id,
        plan_type: p.planType || (p.monthlyTitle?.toLowerCase().includes("overseas") ? "abroad" : "india"),
        monthly_title: p.monthlyTitle,
        annual_title: p.annualTitle || p.monthlyTitle,
        description: p.description,
        monthly_price: Number(p.monthlyPrice || (p.planType === "abroad" ? p.abroadMonthlyPrice : 0)),
        annual_price: Number(p.annualPrice || (p.planType === "abroad" ? p.abroadAnnualPrice : p.monthlyPrice) || 0),
        abroad_monthly_price: Number(p.abroadMonthlyPrice || (p.planType === "abroad" ? p.monthlyPrice : 0)),
        abroad_annual_price: Number(p.abroadAnnualPrice || (p.planType === "abroad" ? p.annualPrice : 0)),
        features: p.features,
        isFree: p.isCurrent ? 1 : 0,
      }));

      const res = await fetch(PLANS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "save",
          plans: payload,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        showPopup("Saved successfully");
        fetchPlans();
      } else {
        showPopup(data.msg || "Save failed", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error", "error");
    }
  };

  // ================= FILTERED PLANS FOR DISPLAY =================
  const displayedPlans = plans.filter((p) => {
    if (activeCategoryTab === "india") return p.planType === "india";
    if (activeCategoryTab === "abroad") return p.planType === "abroad";
    return true;
  });

  const indiaCount = plans.filter((p) => p.planType === "india").length;
  const abroadCount = plans.filter((p) => p.planType === "abroad").length;

  // ================= LOADING =================
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px", color: "#64748b" }}>
        <div className="spinner-border text-primary" role="status" style={{ color: "#5d1d2b" }}></div>
        <p style={{ marginTop: "15px" }}>Loading plans...</p>
      </div>
    );
  }

  /* =====================================================
     UI
  ===================================================== */
  return (
    <div className="admin-container subscribe-admin-container">
      
      {/* POPUP */}
      {popup && (
        <div className={`popup ${popup.type}`}>
          {popup.msg}
        </div>
      )}

      {/* Page Header with Floating Actions */}
      <div className="subscribe-header-row">
        <div>
          <h1>
            <i className="bi bi-card-checklist"></i> Admin Subscription Plans
          </h1>
          <p>Configure pricing, titles, descriptions, and features for India & Abroad plans</p>
        </div>
        <div className="header-actions">
          <button className="btn-add-plan" onClick={handleAdd}>
            <i className="bi bi-plus-lg"></i> Add Plan
          </button>
          <button className="btn-save-all" onClick={handleSave}>
            <i className="bi bi-cloud-arrow-up"></i> Save All Plans
          </button>
        </div>
      </div>

      {/* CATEGORY FILTER TABS */}
      <div style={{ display: "flex", gap: "10px", margin: "0 0 25px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setActiveCategoryTab("all")}
          style={{
            padding: "8px 18px",
            borderRadius: "20px",
            border: activeCategoryTab === "all" ? "2px solid #5d1d2b" : "1px solid #cbd5e1",
            background: activeCategoryTab === "all" ? "#5d1d2b" : "#ffffff",
            color: activeCategoryTab === "all" ? "#ffffff" : "#475569",
            fontWeight: "700",
            fontSize: "13px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)"
          }}
        >
          All Plans ({plans.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveCategoryTab("india")}
          style={{
            padding: "8px 18px",
            borderRadius: "20px",
            border: activeCategoryTab === "india" ? "2px solid #16a34a" : "1px solid #cbd5e1",
            background: activeCategoryTab === "india" ? "#16a34a" : "#ffffff",
            color: activeCategoryTab === "india" ? "#ffffff" : "#475569",
            fontWeight: "700",
            fontSize: "13px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)"
          }}
        >
          🇮🇳 India Domestic ({indiaCount})
        </button>
        <button
          type="button"
          onClick={() => setActiveCategoryTab("abroad")}
          style={{
            padding: "8px 18px",
            borderRadius: "20px",
            border: activeCategoryTab === "abroad" ? "2px solid #2563eb" : "1px solid #cbd5e1",
            background: activeCategoryTab === "abroad" ? "#2563eb" : "#ffffff",
            color: activeCategoryTab === "abroad" ? "#ffffff" : "#475569",
            fontWeight: "700",
            fontSize: "13px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)"
          }}
        >
          🌐 Abroad Overseas ({abroadCount})
        </button>
      </div>

      {/* PLANS GRID */}
      <div className="plans-grid">
        {displayedPlans.map((plan) => {
          const isAbroadCard = plan.planType === "abroad";

          return (
            <div key={plan.id} id={`plan-card-${plan.id}`} className="admin-card" style={{ borderTop: isAbroadCard ? "4px solid #2563eb" : "4px solid #16a34a" }}>
              
              {/* Card Header (ID, Category Dropdown & Free Badge) */}
              <div className="plan-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div className="plan-id-badge">
                  ID: {plan.id}
                </div>
                
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <select
                    value={plan.planType || "india"}
                    onChange={(e) => updatePlan(plan.id, "planType", e.target.value)}
                    style={{
                      fontSize: "12px",
                      fontWeight: "750",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      border: isAbroadCard ? "1.5px solid #2563eb" : "1.5px solid #16a34a",
                      background: isAbroadCard ? "#eff6ff" : "#f0fdf4",
                      color: isAbroadCard ? "#1d4ed8" : "#15803d",
                      cursor: "pointer",
                      outline: "none"
                    }}
                  >
                    <option value="india">🇮🇳 India Category</option>
                    <option value="abroad">🌐 Abroad Category</option>
                  </select>

                  {plan.isCurrent && (
                    <div className="current-badge">
                      Free Plan
                    </div>
                  )}
                </div>
              </div>

              {/* 1 Year Plan Title */}
              <div style={{ fontWeight: 'bold', fontSize: '0.82rem', color: '#5d1d2b', marginTop: '6px', marginBottom: '3px' }}>
                1 Year Plan Title:
              </div>
              <input
                type="text"
                placeholder={isAbroadCard ? "e.g. 1 YEAR SUBSCRIPTION + OVERSEAS" : "e.g. 1 YEAR SUBSCRIPTION + COURIER"}
                value={plan.monthlyTitle}
                onChange={(e) => updatePlan(plan.id, "monthlyTitle", e.target.value)}
              />

              {/* 2 Years Plan Title */}
              <div style={{ fontWeight: 'bold', fontSize: '0.82rem', color: '#5d1d2b', marginTop: '6px', marginBottom: '3px' }}>
                2 Years Plan Title:
              </div>
              <input
                type="text"
                placeholder={isAbroadCard ? "e.g. 2 YEARS SUBSCRIPTION + OVERSEAS" : "e.g. 2 YEARS SUBSCRIPTION + COURIER"}
                value={plan.annualTitle}
                onChange={(e) => updatePlan(plan.id, "annualTitle", e.target.value)}
              />

              {/* Pricing Section Based on Plan Type */}
              {!isAbroadCard ? (
                /* India Local Rates */
                <div style={{ background: "#f0fdf4", padding: "12px", borderRadius: "10px", border: "1px solid #bbf7d0", marginBottom: "12px", marginTop: "6px" }}>
                  <div style={{ fontWeight: "750", fontSize: "0.82rem", color: "#166534", marginBottom: "8px" }}>
                    🇮🇳 India Domestic Rates (₹):
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <div>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#14532d" }}>1 Year Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 600"
                        value={plan.monthlyPrice}
                        onChange={(e) => updatePlan(plan.id, "monthlyPrice", e.target.value)}
                        style={{ fontSize: "13px", padding: "6px", background: "#ffffff" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#14532d" }}>2 Years Price (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 1200"
                        value={plan.annualPrice}
                        onChange={(e) => updatePlan(plan.id, "annualPrice", e.target.value)}
                        style={{ fontSize: "13px", padding: "6px", background: "#ffffff" }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Abroad Overseas Rates */
                <div style={{ background: "#eff6ff", padding: "12px", borderRadius: "10px", border: "1px solid #bfdbfe", marginBottom: "12px", marginTop: "6px" }}>
                  <div style={{ fontWeight: "750", fontSize: "0.82rem", color: "#1e40af", marginBottom: "8px" }}>
                    🌐 Abroad Overseas Rates (₹):
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <div>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#1e3a8a" }}>1 Year Abroad (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 3000"
                        value={plan.abroadMonthlyPrice || plan.monthlyPrice}
                        onChange={(e) => updatePlan(plan.id, "abroadMonthlyPrice", e.target.value)}
                        style={{ fontSize: "13px", padding: "6px", background: "#ffffff" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#1e3a8a" }}>2 Years Abroad (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 6000"
                        value={plan.abroadAnnualPrice || plan.annualPrice}
                        onChange={(e) => updatePlan(plan.id, "abroadAnnualPrice", e.target.value)}
                        style={{ fontSize: "13px", padding: "6px", background: "#ffffff" }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#5d1d2b', marginBottom: '4px' }}>
                Description:
              </div>
              <textarea
                placeholder="Provide a brief description of the plan"
                value={plan.description}
                onChange={(e) => updatePlan(plan.id, "description", e.target.value)}
                rows={2}
              />

              {/* Features Title */}
              <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#5d1d2b', marginBottom: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px' }}>
                Key Features:
              </div>

              {/* Features Listing */}
              <div className="features-section">
                {plan.features.map((feature, index) => (
                  <div key={`${plan.id}-${index}`} className="feature-item">
                    <input
                      type="text"
                      placeholder={`Feature ${index + 1}`}
                      value={feature}
                      onChange={(e) => updateFeature(plan.id, index, e.target.value)}
                    />
                    <button
                      className="feature-delete-btn"
                      onClick={() => deleteFeature(plan.id, index)}
                      title="Remove Feature"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Feature Button */}
              <button className="feature-add-btn" onClick={() => addFeature(plan.id)}>
                <i className="bi bi-plus"></i> Add Feature
              </button>

              {/* Plan Card Actions */}
              {!plan.isCurrent && (
                <div className="plans-card-actions" style={{ marginTop: "12px", display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="edit-btn"
                    onClick={() => handleEditPlan(plan)}
                    style={{
                      background: "#eff6ff",
                      color: "#2563eb",
                      border: "1px solid #dbeafe",
                      padding: "8px 16px",
                      borderRadius: "10px",
                      fontWeight: "700",
                      fontSize: "13px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                  >
                    <i className="bi bi-pencil"></i> Edit Plan
                  </button>
                  <button className="delete-btn" onClick={() => deletePlan(plan.id)}>
                    <i className="bi bi-trash3"></i> Delete Plan
                  </button>
                </div>
              )}
              
            </div>
          );
        })}
      </div>

      {/* Global Footer Actions */}
      <div className="global-footer-save">
        <button className="btn-save-all" onClick={handleSave}>
          <i className="bi bi-cloud-arrow-up"></i> Save All Plans
        </button>
      </div>

    </div>
  );
}

export default AdminSubscribe;
